import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import Question from "../../../../api/Question";
import Location from "../../../../api/Location";
import Input from "../../../../components/form/Input";
import MultiSelect from "../../../../components/MultiSelect";
import ConfirmationModal from "../../../../components/modals/ConfirmationModal";
import { TrashIcon, PlusIcon, XMarkIcon, PencilIcon } from "@heroicons/react/24/outline";

const questionTypes = [
  { value: "radiobutton", label: "Radio Button" },
  { value: "checkbox", label: "Checkbox" },
  { value: "text", label: "Text Input" },
  { value: "textarea", label: "Text Area" },
];

// Global option for all locations
const GLOBAL_OPTION = { label: "Global (All Locations)", value: "global" };

const QuestionsPanel = ({ callback = false }) => {
  // Locations
  const [locations, setLocations] = useState([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);

  // COMMON Location Selection for creating questions
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [locationError, setLocationError] = useState(false);

  // Existing questions from API
  const [existingQuestions, setExistingQuestions] = useState([]);
  const [existingQuestionsOptions, setExistingQuestionsOptions] = useState([]);
  const [isLoadingExisting, setIsLoadingExisting] = useState(false);

  // Filter by location for viewing
  const [filterLocation, setFilterLocation] = useState(null);

  // Form fields for new question (without location - location is common)
  const [questionText, setQuestionText] = useState("");
  const [questionType, setQuestionType] = useState(questionTypes[0]);
  const [options, setOptions] = useState(["yes", "no"]);
  const [newOption, setNewOption] = useState("");

  // Form errors
  const [questionError, setQuestionError] = useState(false);
  const [optionsError, setOptionsError] = useState(false);

  // Selected existing question to add
  const [selectedExistingQuestion, setSelectedExistingQuestion] = useState(null);

  // Questions array to submit
  const [questionsToSubmit, setQuestionsToSubmit] = useState([]);

  // Submit loading state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Delete confirmation modal for questions to submit list
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editFormData, setEditFormData] = useState({
    question: "",
    options: [],
    type: questionTypes[0],
  });
  const [editNewOption, setEditNewOption] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  // Delete existing question modal
  const [openDeleteExistingModal, setOpenDeleteExistingModal] = useState(false);
  const [existingQuestionToDelete, setExistingQuestionToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load locations
  const loadLocations = async () => {
    setIsLoadingLocations(true);
    try {
      const response = await Location.getAll();
      if (response?.data?.data) {
        const locationOptions = response.data.data.map((loc) => ({
          label: loc.name,
          value: loc.id,
        }));
        // Add Global option at the beginning
        setLocations([GLOBAL_OPTION, ...locationOptions]);
      }
    } catch (error) {
      console.error("Error loading locations:", error);
      toast.error("Failed to load locations");
    } finally {
      setIsLoadingLocations(false);
    }
  };

  // Fetch all existing questions from API
  const loadExistingQuestions = async () => {
    setIsLoadingExisting(true);
    try {
      const response = await Question.getAll();
      let questionsData = [];

      // Handle different response formats
      // API returns { content: [...], pageable: {...}, ... }
      if (response?.data?.content) {
        questionsData = response.data.content;
      } else if (response?.data?.data) {
        questionsData = response.data.data;
      } else if (Array.isArray(response?.data)) {
        questionsData = response.data;
      } else if (response?.data && typeof response.data === 'object') {
        questionsData = response.data.questions || response.data.items || response.data.results || [];
      }

      setExistingQuestions(questionsData);

      // Format for MultiSelect dropdown
      const options = questionsData.map((q) => ({
        label: q.question || q.text || q.title,
        value: q.id,
        data: q,
      }));
      setExistingQuestionsOptions(options);
    } catch (error) {
      console.error("Error loading existing questions:", error);
      setExistingQuestions([]);
      setExistingQuestionsOptions([]);
    } finally {
      setIsLoadingExisting(false);
    }
  };

  // Fetch questions by location
  const loadQuestionsByLocation = async (locationId) => {
    setIsLoadingExisting(true);
    try {
      const response = await Question.getByLocationId(locationId);
      let questionsData = [];

      // Handle different response formats
      // API returns { content: [...], pageable: {...}, ... }
      if (response?.data?.content) {
        questionsData = response.data.content;
      } else if (response?.data?.data) {
        questionsData = response.data.data;
      } else if (Array.isArray(response?.data)) {
        questionsData = response.data;
      } else if (response?.data && typeof response.data === 'object') {
        questionsData = response.data.questions || response.data.items || response.data.results || [];
      }

      setExistingQuestions(questionsData);
    } catch (error) {
      console.error("Error loading questions by location:", error);
      setExistingQuestions([]);
    } finally {
      setIsLoadingExisting(false);
    }
  };

  // Handle filter location change
  const handleFilterLocationChange = (selected) => {
    setFilterLocation(selected);
    if (selected) {
      loadQuestionsByLocation(selected.value);
    } else {
      loadExistingQuestions();
    }
  };

  useEffect(() => {
    loadLocations();
    loadExistingQuestions();
  }, []);

  // Check if Global option is selected
  const isGlobalSelected = (selected) => {
    if (!selected) return false;
    if (Array.isArray(selected)) {
      return selected.some((loc) => loc.value === "global");
    }
    return selected.value === "global";
  };

  // Handle common location selection change
  const handleLocationChange = (selected) => {
    let newSelectedLocations = [];

    if (selected && selected.length > 0) {
      const lastSelected = selected[selected.length - 1];
      if (lastSelected.value === "global") {
        newSelectedLocations = [GLOBAL_OPTION];
      } else {
        // If a specific location is selected, remove Global if it exists
        newSelectedLocations = selected.filter((loc) => loc.value !== "global");
      }
    } else {
      newSelectedLocations = selected || [];
    }

    setSelectedLocations(newSelectedLocations);
    setLocationError(false);

    // Update all questions in the list with the new locations
    if (questionsToSubmit.length > 0 && newSelectedLocations.length > 0) {
      const isGlobal = newSelectedLocations.some((loc) => loc.value === "global");
      const locationIds = isGlobal ? null : newSelectedLocations.map((loc) => loc.value);
      const locationNames = isGlobal ? ["Global (All Locations)"] : newSelectedLocations.map((loc) => loc.label);

      const updatedQuestions = questionsToSubmit.map((q) => ({
        ...q,
        locationId: locationIds,
        locationNames: locationNames,
        isGlobal: isGlobal,
      }));

      setQuestionsToSubmit(updatedQuestions);
    }
  };

  // Add option to the options list
  const handleAddOption = () => {
    if (newOption.trim() === "") return;
    if (options.includes(newOption.trim())) {
      toast.error("This option already exists");
      return;
    }
    setOptions([...options, newOption.trim()]);
    setNewOption("");
    setOptionsError(false);
  };

  // Remove option from the list
  const handleRemoveOption = (index) => {
    const updated = options.filter((_, i) => i !== index);
    setOptions(updated);
  };

  // Reset question form (not location)
  const resetQuestionForm = () => {
    setQuestionText("");
    setQuestionType(questionTypes[0]);
    setOptions(["yes", "no"]);
    setNewOption("");
    setQuestionError(false);
    setOptionsError(false);
  };

  // Add new question to the list
  const handleAddNewQuestion = () => {
    let hasError = false;

    if (!selectedLocations || selectedLocations.length === 0) {
      setLocationError(true);
      toast.error("Please select locations first");
      hasError = true;
    }

    if (questionText.trim() === "") {
      setQuestionError(true);
      hasError = true;
    }

    if (options.length === 0 && (questionType.value === "radiobutton" || questionType.value === "checkbox")) {
      setOptionsError(true);
      hasError = true;
    }

    if (hasError) return;

    // Check if Global is selected - if so, locationId should be null
    const isGlobal = isGlobalSelected(selectedLocations);
    const locationIds = isGlobal ? null : selectedLocations.map((loc) => loc.value);
    const locationNames = isGlobal ? ["Global (All Locations)"] : selectedLocations.map((loc) => loc.label);

    const newQuestionObj = {
      id: `new-${Date.now()}`,
      locationId: locationIds,
      locationNames: locationNames,
      question: questionText.trim(),
      options: questionType.value === "radiobutton" || questionType.value === "checkbox" ? [...options] : [],
      type: questionType.value,
      isNew: true,
      isGlobal: isGlobal,
    };

    setQuestionsToSubmit([...questionsToSubmit, newQuestionObj]);
    resetQuestionForm(); // Only reset question form, keep location
    toast.success("Question added to list");
  };

  // Add existing question to the list (will use the common selected locations)
  const handleSelectExistingQuestion = (selected) => {
    if (!selected) return;

    if (!selectedLocations || selectedLocations.length === 0) {
      setLocationError(true);
      toast.error("Please select locations first");
      setSelectedExistingQuestion(null);
      return;
    }

    // Check if already added
    const alreadyAdded = questionsToSubmit.some(
      (q) => q.existingId === selected.value
    );

    if (alreadyAdded) {
      toast.error("This question is already in your list");
      setSelectedExistingQuestion(null);
      return;
    }

    const existingQ = selected.data;

    // Use the common selected locations
    const isGlobal = isGlobalSelected(selectedLocations);
    const locationIds = isGlobal ? null : selectedLocations.map((loc) => loc.value);
    const locationNames = isGlobal ? ["Global (All Locations)"] : selectedLocations.map((loc) => loc.label);

    const questionObj = {
      id: `existing-${selected.value}`,
      existingId: selected.value,
      locationId: locationIds,
      locationNames: locationNames,
      question: existingQ.question || existingQ.text || existingQ.title,
      options: existingQ.options || [],
      type: existingQ.type || "radiobutton",
      isNew: false,
      isGlobal: isGlobal,
    };

    setQuestionsToSubmit([...questionsToSubmit, questionObj]);
    setSelectedExistingQuestion(null);
    toast.success("Existing question added to list");
  };

  // Remove question from the list
  const handleRemoveQuestion = (index) => {
    const updated = questionsToSubmit.filter((_, i) => i !== index);
    setQuestionsToSubmit(updated);
    toast.success("Question removed from list");
  };

  // Open delete confirmation for questions to submit
  const openDeleteConfirmation = (index) => {
    setQuestionToDelete(index);
    setOpenDeleteModal(true);
  };

  // Confirm delete for questions to submit
  const confirmDelete = () => {
    if (questionToDelete !== null) {
      handleRemoveQuestion(questionToDelete);
    }
    setOpenDeleteModal(false);
    setQuestionToDelete(null);
  };

  // Submit questions to API
  const handleSubmitQuestions = async () => {
    if (questionsToSubmit.length === 0) {
      toast.error("Please add at least one question");
      return;
    }

    setIsSubmitting(true);
    try {
      // Format the payload as required by the API
      const payload = questionsToSubmit.map((q) => ({
        locationId: q.locationId,
        question: q.question,
        options: q.options,
        type: q.type,
      }));

      const response = await Question.create(payload);
      if (response?.data) {
        toast.success("Questions submitted successfully!");
        setQuestionsToSubmit([]);
        setSelectedLocations([]); // Reset locations after successful submit
        loadExistingQuestions(); // Refresh the list
      }
    } catch (error) {
      console.error("Error submitting questions:", error);
      toast.error("Failed to submit questions. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Clear all questions from the list
  const handleClearAll = () => {
    setQuestionsToSubmit([]);
    toast.success("All questions cleared");
  };

  // ============ EDIT FUNCTIONALITY ============

  // Open edit modal
  const handleEditQuestion = (question) => {
    setEditingQuestion(question);

    // Find the question type
    const typeOption = questionTypes.find((t) => t.value === question.type) || questionTypes[0];

    setEditFormData({
      question: question.question || question.text || question.title || "",
      options: question.options || [],
      type: typeOption,
    });
    setEditNewOption("");
    setShowEditModal(true);
  };

  // Add option in edit form
  const handleEditAddOption = () => {
    if (editNewOption.trim() === "") return;
    if (editFormData.options.includes(editNewOption.trim())) {
      toast.error("This option already exists");
      return;
    }
    setEditFormData({
      ...editFormData,
      options: [...editFormData.options, editNewOption.trim()],
    });
    setEditNewOption("");
  };

  // Remove option in edit form
  const handleEditRemoveOption = (index) => {
    const updated = editFormData.options.filter((_, i) => i !== index);
    setEditFormData({ ...editFormData, options: updated });
  };

  // Submit edit
  const handleUpdateQuestion = async () => {
    if (editFormData.question.trim() === "") {
      toast.error("Question text is required");
      return;
    }
    if (
      editFormData.options.length === 0 &&
      (editFormData.type.value === "radiobutton" || editFormData.type.value === "checkbox")
    ) {
      toast.error("Please add at least one option");
      return;
    }

    setIsUpdating(true);
    try {
      // Use the original locationIds from the editing question
      const originalLocationIds = editingQuestion.locationIds || editingQuestion.locationId || editingQuestion.location_id;

      const payload = {
        locationIds: originalLocationIds,
        question: editFormData.question.trim(),
        options: editFormData.type.value === "radiobutton" || editFormData.type.value === "checkbox"
          ? editFormData.options
          : [],
        type: editFormData.type.value,
      };

      const response = await Question.update(editingQuestion.id, payload);
      if (response?.data) {
        toast.success("Question updated successfully!");
        setShowEditModal(false);
        setEditingQuestion(null);
        loadExistingQuestions(); // Refresh the list
      }
    } catch (error) {
      console.error("Error updating question:", error);
      toast.error("Failed to update question. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  // ============ DELETE EXISTING QUESTION FUNCTIONALITY ============

  // Open delete confirmation for existing question
  const handleDeleteExistingQuestion = (question) => {
    setExistingQuestionToDelete(question);
    setOpenDeleteExistingModal(true);
  };

  // Confirm delete existing question
  const confirmDeleteExisting = async () => {
    if (!existingQuestionToDelete) return;

    setIsDeleting(true);
    try {
      const response = await Question.delete(existingQuestionToDelete.id);
      if (response?.data) {
        toast.success("Question deleted successfully!");
        loadExistingQuestions(); // Refresh the list
      }
    } catch (error) {
      console.error("Error deleting question:", error);
      toast.error("Failed to delete question. Please try again.");
    } finally {
      setIsDeleting(false);
      setOpenDeleteExistingModal(false);
      setExistingQuestionToDelete(null);
    }
  };

  // Get location names from IDs
  const getLocationNames = (question) => {
    const locationIds = question.locationIds || question.locationId || question.location_id;

    // If locationIds is null or empty array, it's a Global question
    if (locationIds === null || (Array.isArray(locationIds) && locationIds.length === 0)) {
      return ["Global (All Locations)"];
    }

    if (!Array.isArray(locationIds)) return [];

    return locations
      .filter((loc) => loc.value !== "global" && locationIds.includes(loc.value))
      .map((loc) => loc.label);
  };

  return (
    <div className="my-8">
      {/* Delete modal for questions to submit */}
      <ConfirmationModal
        openTrigger={openDeleteModal}
        closeTrigger={() => setOpenDeleteModal(false)}
        type="error"
        title="Confirm Action"
        confirmText="Delete"
        description="Are you sure you want to remove this question from the list?"
        confirm={confirmDelete}
      />

      {/* Delete modal for existing questions */}
      {openDeleteExistingModal && existingQuestionToDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={() => {
                setOpenDeleteExistingModal(false);
                setExistingQuestionToDelete(null);
              }}
            />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Question</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    This action cannot be undone.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setOpenDeleteExistingModal(false);
                    setExistingQuestionToDelete(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-700 mb-2">
                  Are you sure you want to delete this question?
                </p>
                <div className="bg-gray-50 rounded-md p-3 border border-gray-200">
                  <p className="text-sm font-medium text-gray-900">
                    {existingQuestionToDelete.question || existingQuestionToDelete.text || existingQuestionToDelete.title}
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setOpenDeleteExistingModal(false);
                    setExistingQuestionToDelete(null);
                  }}
                  disabled={isDeleting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-all disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteExisting}
                  disabled={isDeleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-md transition-all disabled:opacity-50 flex items-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <i className="fad fa-circle-notch fa-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <TrashIcon className="h-4 w-4" />
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowEditModal(false)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Edit Question</h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="flex flex-col gap-4">
                {/* Question Text */}
                <div>
                  <Input
                    name="editQuestionText"
                    label="Question Text"
                    type="text"
                    value={editFormData.question}
                    placeholder="Enter your question here..."
                    darmo={true}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, question: e.target.value })
                    }
                  />
                </div>

                {/* Question Type */}
                <div>
                  <p className="text-md font-medium mb-1">Question Type</p>
                  <div className="border border-warmgray-300 rounded-md">
                    <MultiSelect
                      options={questionTypes}
                      onChange={(selected) =>
                        setEditFormData({ ...editFormData, type: selected })
                      }
                      value={editFormData.type}
                      menuPlacement="bottom"
                      placeholder="Select question type..."
                    />
                  </div>
                </div>

                {/* Options */}
                {(editFormData.type?.value === "radiobutton" ||
                  editFormData.type?.value === "checkbox") && (
                    <div>
                      <p className="text-md font-medium mb-1">
                        Options <span className="text-red-500">*</span>
                      </p>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {editFormData.options.map((opt, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-1 bg-primary-100 text-primary-800 px-2 py-1 rounded-full text-sm"
                          >
                            <span>{opt}</span>
                            <button
                              type="button"
                              onClick={() => handleEditRemoveOption(index)}
                              className="hover:text-red-500"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={editNewOption}
                          onChange={(e) => setEditNewOption(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleEditAddOption();
                            }
                          }}
                          placeholder="Add an option..."
                          className="flex-1 border border-warmgray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <button
                          type="button"
                          onClick={handleEditAddOption}
                          className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-sm font-medium transition-all"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  )}

                {/* Actions */}
                <div className="flex justify-end gap-3 mt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleUpdateQuestion}
                    disabled={isUpdating}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-400 rounded-md transition-all disabled:opacity-50 flex items-center gap-2"
                  >
                    {isUpdating ? (
                      <>
                        <i className="fad fa-circle-notch fa-spin" />
                        Updating...
                      </>
                    ) : (
                      "Update Question"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-6">
        <p className="text-xl font-bold">Questions Management</p>

        {/* Existing Questions Section */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <p className="text-lg font-semibold">Existing Questions</p>

            {/* Filter by Location */}
            <div className="md:w-64">
              <div className="border border-warmgray-300 rounded-md bg-white">
                <MultiSelect
                  options={locations.filter(loc => loc.value !== "global")}
                  onChange={handleFilterLocationChange}
                  value={filterLocation}
                  menuPlacement="bottom"
                  isLoading={isLoadingLocations}
                  placeholder="Filter by location..."
                  isClearable={true}
                />
              </div>
            </div>
          </div>

          {isLoadingExisting ? (
            <div className="flex items-center gap-2">
              <i className="fad fa-circle-notch fa-spin" />
              <span>Loading existing questions...</span>
            </div>
          ) : existingQuestions.length > 0 ? (
            <div className="overflow-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="py-2 px-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">
                      #
                    </th>
                    <th className="py-2 px-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">
                      Question
                    </th>
                    <th className="py-2 px-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">
                      Type
                    </th>
                    <th className="py-2 px-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">
                      Options
                    </th>
                    <th className="py-2 px-3 text-right text-sm font-semibold text-gray-900 whitespace-nowrap">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {existingQuestions.map((q, index) => (
                    <tr key={q.id}>
                      <td className="py-2 px-3 text-sm text-gray-500">
                        {index + 1}
                      </td>
                      <td className="py-2 px-3 text-sm text-gray-900">
                        {q.question || q.text || q.title}
                      </td>
                      <td className="py-2 px-3 text-sm">
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {q.type}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-sm text-gray-500">
                        {q.options?.join(", ") || "-"}
                      </td>
                      <td className="py-2 px-3 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            className="text-blue-500 hover:text-blue-700 transition-all"
                            onClick={() => handleEditQuestion(q)}
                            title="Edit question"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            type="button"
                            className="text-red-500 hover:text-red-700 transition-all"
                            onClick={() => handleDeleteExistingQuestion(q)}
                            title="Delete question"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 italic">
              No questions found.{" "}
              {filterLocation && "Try removing the location filter or add new questions."}
            </p>
          )}
        </div>

        {/* Create Questions Section */}
        <div className="border border-gray-200 p-4 rounded-lg">
          <p className="text-lg font-semibold mb-4">Create New Questions</p>

          <div className="flex flex-col gap-4">
            {/* COMMON Location Selection - at the top */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-md font-semibold mb-2 text-blue-800">
                Step 1: Select Locations for Questions
              </p>
              <p className="text-sm text-blue-600 mb-3">
                All questions you add below will be created for these selected locations.
              </p>
              <div className="md:max-w-md">
                <div className={`border rounded-md ${locationError ? 'border-red-500' : 'border-warmgray-300'} bg-white`}>
                  <MultiSelect
                    key="locations"
                    options={locations}
                    onChange={handleLocationChange}
                    value={selectedLocations}
                    menuPlacement="bottom"
                    isLoading={isLoadingLocations}
                    placeholder="Select locations or Global..."
                    isMulti={true}
                  />
                </div>
                {locationError && (
                  <p className="text-red-500 text-sm mt-1">Please select at least one location or Global</p>
                )}
                {selectedLocations.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    <span className="text-sm text-gray-600">Selected: </span>
                    {selectedLocations.map((loc, i) => (
                      <span
                        key={i}
                        className={`px-2 py-0.5 rounded text-xs font-medium ${loc.value === "global"
                          ? "bg-green-100 text-green-800"
                          : "bg-primary-100 text-primary-800"
                          }`}
                      >
                        {loc.label}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Question Creation */}
            <div className="border-t border-gray-200 pt-4">
              <p className="text-md font-semibold mb-3">Step 2: Add Questions</p>

              <div className="flex flex-col gap-4">
                {/* Question Text */}
                <div className="md:max-w-md">
                  <Input
                    name="questionText"
                    label="Question Text"
                    type="text"
                    value={questionText}
                    error={questionError && "Question text is required"}
                    placeholder="Enter your question here..."
                    darmo={true}
                    onChange={(e) => {
                      setQuestionText(e.target.value);
                      setQuestionError(false);
                    }}
                  />
                </div>

                {/* Question Type */}
                <div className="flex flex-col md:max-w-md">
                  <p className="text-md font-medium mb-1">Question Type</p>
                  <div className="border border-warmgray-300 rounded-md">
                    <MultiSelect
                      key="questionType"
                      options={questionTypes}
                      onChange={(selected) => setQuestionType(selected)}
                      value={questionType}
                      menuPlacement="bottom"
                      placeholder="Select question type..."
                    />
                  </div>
                </div>

                {/* Options (for radiobutton/checkbox types) */}
                {(questionType?.value === "radiobutton" || questionType?.value === "checkbox") && (
                  <div className="md:max-w-md">
                    <p className="text-md font-medium mb-1">
                      Options <span className="text-red-500">*</span>
                    </p>

                    {/* Current options */}
                    <div className="flex flex-wrap gap-2 mb-2">
                      {options.map((opt, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-1 bg-primary-100 text-primary-800 px-2 py-1 rounded-full text-sm"
                        >
                          <span>{opt}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveOption(index)}
                            className="hover:text-red-500"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>

                    {/* Add new option */}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newOption}
                        onChange={(e) => setNewOption(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddOption();
                          }
                        }}
                        placeholder="Add an option..."
                        className="flex-1 border border-warmgray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <button
                        type="button"
                        onClick={handleAddOption}
                        className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-sm font-medium transition-all"
                      >
                        Add
                      </button>
                    </div>
                    {optionsError && (
                      <p className="text-red-500 text-sm mt-1">Please add at least one option</p>
                    )}
                  </div>
                )}

                {/* Add Question Button */}
                <div>
                  <button
                    type="button"
                    className="cursor-pointer font-bold text-sm px-4 py-2 rounded-md bg-primary-500 text-white hover:bg-primary-400 transition-all disabled:opacity-50 flex items-center gap-2"
                    onClick={handleAddNewQuestion}
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add Question to List
                  </button>
                </div>

                {/* Select from existing questions */}
                {existingQuestionsOptions.length > 0 && (
                  <div className="border-t border-gray-200 pt-4 mt-2">
                    <div className="flex flex-col md:max-w-md">
                      <p className="text-md font-medium mb-1">
                        Or Select from Existing Questions
                      </p>
                      <p className="text-sm text-gray-500 mb-2">
                        The selected question will be added with your selected locations above.
                      </p>
                      <div className="border border-warmgray-300 rounded-md">
                        <MultiSelect
                          key="existingQuestions"
                          options={existingQuestionsOptions}
                          onChange={handleSelectExistingQuestion}
                          value={selectedExistingQuestion}
                          menuPlacement="bottom"
                          isLoading={isLoadingExisting}
                          placeholder="Select an existing question..."
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Questions to Submit Section */}
        <div className="border border-gray-200 p-4 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <p className="text-lg font-semibold">
              Questions to Submit ({questionsToSubmit.length})
            </p>
            {questionsToSubmit.length > 0 && (
              <button
                type="button"
                className="text-sm text-red-500 hover:text-red-700 transition-all"
                onClick={handleClearAll}
              >
                Clear All
              </button>
            )}
          </div>

          {questionsToSubmit.length === 0 ? (
            <p className="text-gray-500 italic">
              No questions added yet. Select locations first, then add questions above.
            </p>
          ) : (
            <div className="overflow-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="py-2 px-3 text-left text-sm font-semibold text-gray-900 w-12 whitespace-nowrap">
                      #
                    </th>
                    <th className="py-2 px-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">
                      Question
                    </th>
                    <th className="py-2 px-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">
                      Locations
                    </th>
                    <th className="py-2 px-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">
                      Type
                    </th>
                    <th className="py-2 px-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">
                      Options
                    </th>
                    <th className="py-2 px-3 text-right text-sm font-semibold text-gray-900 w-20 whitespace-nowrap">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {questionsToSubmit.map((q, index) => (
                    <tr key={q.id}>
                      <td className="py-2 px-3 text-sm text-gray-500">
                        {index + 1}
                      </td>
                      <td className="py-2 px-3 text-sm text-gray-900">
                        {q.question}
                      </td>
                      <td className="py-2 px-3 text-sm text-gray-500">
                        <div className="flex flex-wrap gap-1">
                          {q.locationNames?.map((name, i) => (
                            <span
                              key={i}
                              className={`px-2 py-0.5 rounded text-xs ${name === "Global (All Locations)"
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100"
                                }`}
                            >
                              {name}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-sm">
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {q.type}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-sm text-gray-500">
                        {q.options?.join(", ") || "-"}
                      </td>
                      <td className="py-2 px-3 text-right">
                        <button
                          type="button"
                          className="text-red-500 hover:text-red-700 transition-all"
                          onClick={() => openDeleteConfirmation(index)}
                          title="Remove question"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Submit Button */}
          <div className="mt-6 flex justify-end">
            <button
              type="button"
              className="cursor-pointer font-bold text-sm px-6 py-2 rounded-md bg-primary-500 text-white hover:bg-primary-400 transition-all disabled:opacity-50 flex items-center gap-2"
              onClick={handleSubmitQuestions}
              disabled={questionsToSubmit.length === 0 || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <i className="fad fa-circle-notch fa-spin" />
                  Submitting...
                </>
              ) : (
                <>Submit Questions</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionsPanel;
